/**
 * Single group standings card — layout aligned with web GroupTable (header band, row tints, computed table).
 */

import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import type { WorldCupNationalTeamStandingRow } from '../../../src/worldcup/types/worldCupTournamentStandings.types';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { getWorldCupGroupStageTableCardStyles } from './WorldCupGroupStageTableCard.styles';
import {
  STANDINGS_MODAL_COPY,
  STANDINGS_TABLE_COLS,
  THIRD_PLACE_QUALIFY_LIMIT,
} from './worldCupStandingsModal.constants';
import {
  formatGd,
  getGdStyle,
  getPositionBarStyle,
  getDataRowStyle,
} from './worldCupGroupStageTableCard.helper';

interface WorldCupGroupStageTableCardProps {
  groupLetter: string;
  teams: readonly WorldCupNationalTeamStandingRow[];
  hasResults: boolean;
  isThirdPlaceRanking?: boolean;
}

export const WorldCupGroupStageTableCard: React.FC<WorldCupGroupStageTableCardProps> = ({
  groupLetter,
  teams,
  hasResults,
  isThirdPlaceRanking = false,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getWorldCupGroupStageTableCardStyles(theme), [theme]);
  const c = STANDINGS_TABLE_COLS;
  const qualifyOpts = isThirdPlaceRanking
    ? { qualifyLimit: THIRD_PLACE_QUALIFY_LIMIT, showPossibleThird: false }
    : undefined;

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Text style={styles.groupTitle}>
          {isThirdPlaceRanking
            ? STANDINGS_MODAL_COPY.THIRD_PLACE_HEADING
            : `${STANDINGS_MODAL_COPY.GROUP_HEADING_PREFIX} ${groupLetter}`}
        </Text>
      </View>

      <View style={styles.tableBody}>
        <View style={styles.headerRow}>
          <View style={styles.colTeam}>
            <Text style={styles.headerTextTeam}>{c.TEAM}</Text>
          </View>
          <View style={styles.colStat}>
            <Text style={styles.headerText}>{c.P}</Text>
          </View>
          <View style={styles.colStat}>
            <Text style={styles.headerText}>{c.W}</Text>
          </View>
          <View style={styles.colStat}>
            <Text style={styles.headerText}>{c.D}</Text>
          </View>
          <View style={styles.colStat}>
            <Text style={styles.headerText}>{c.L}</Text>
          </View>
          <View style={styles.colGd}>
            <Text style={styles.headerText}>{c.GD}</Text>
          </View>
          <View style={styles.colPts}>
            <Text style={styles.headerTextPts}>{c.PTS}</Text>
          </View>
        </View>

        {teams.map((team, index) => {
          const pos = index + 1;
          const isLast = index === teams.length - 1;
          const rowKey = `${groupLetter}-${index}-${team.countryCode || team.name || 'team'}`;
          return (
            <View key={rowKey} style={getDataRowStyle(index, hasResults, isLast, styles, qualifyOpts)}>
              <View style={[styles.positionBar, getPositionBarStyle(pos, styles, qualifyOpts)]} />
              <View style={styles.colTeam}>
                <CountryFlag
                  countryCode={team.countryCode}
                  kitFallback={getTeamKitConfig(team.name)}
                  size="compact"
                />
                <Text
                  style={styles.teamName}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {team.name}
                </Text>
              </View>
              <View style={styles.colStat}>
                <Text style={styles.cellText}>{team.played}</Text>
              </View>
              <View style={styles.colStat}>
                <Text style={styles.cellText}>{team.won}</Text>
              </View>
              <View style={styles.colStat}>
                <Text style={styles.cellText}>{team.drawn}</Text>
              </View>
              <View style={styles.colStat}>
                <Text style={styles.cellText}>{team.lost}</Text>
              </View>
              <View style={styles.colGd}>
                <Text style={getGdStyle(team.goalDifference, styles)}>
                  {formatGd(team.goalDifference)}
                </Text>
              </View>
              <View style={styles.colPts}>
                <Text style={styles.cellPts}>{team.points}</Text>
              </View>
            </View>
          );
        })}
      </View>

      <View style={styles.legend}>
        {isThirdPlaceRanking ? (
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, styles.legendDotQualified]} />
            <Text style={styles.legendText}>{STANDINGS_MODAL_COPY.LEGEND_THIRD_PLACE_QUALIFIED}</Text>
          </View>
        ) : (
          <>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, styles.legendDotQualified]} />
              <Text style={styles.legendText}>{STANDINGS_MODAL_COPY.LEGEND_QUALIFIED}</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, styles.legendDotPossible]} />
              <Text style={styles.legendText}>{STANDINGS_MODAL_COPY.LEGEND_POSSIBLE_3RD}</Text>
            </View>
          </>
        )}
      </View>
    </View>
  );
};
